import talViewProctoringType from '../../constants/TalViewProctoringType';
import constants from '../../qti/Constants';
import assessmentReader from '../../store/readHelpers/AssessmentReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import settingsReader from '../../store/readHelpers/SettingsReader';
import globalsService from '../GlobalsService';
import marketplaceSettingsService from '../MarketplaceSettingsService';
import navigationService from '../NavigationService';
import utilService from '../UtilService';
import { safeBrowserLinks } from '../api/ApiConstants';
import storeManager from '../../store/StoreManager';
import uiActions from '../../actions/UIActions';
import uiReader from '../../store/readHelpers/uiReader';
import apiService from '../api/ApiService';

const clientPingInterval = 100;
const proctorClientMaxInitTime = 120000;
const sessionJoinedTimeoutTime = 10000;

let hasTalviewInitAllocatedTimePassed = false;
let hasTTJoinedSession = false;

const talViewService = {
    async init() {
        try {
            this.createScript();
            this.createProctoringSession();
            await this.initProctorClient();
            this.createProctorClientEventListener();
        } catch (error) {
            console.error(error);
        }
    },

    createScript() {
        const scriptUrl = constants.staticLibPaths.TALVIEW_SCRIPT;

        /* eslint-disable no-unused-expressions, prefer-rest-params, no-sequences, no-param-reassign, func-names */
        // linting disabled because the original script is used from the documentation:
        // https://talview.notion.site/9f641b5b16604d0a901d88c9eb604e90?v=6670868b6eb74ba08390dc86f13412af
        (function (i, s, o, g, r, a, m) {
            i.TalviewProctor = r;
            i[r] = i[r] || function () {
                (i[r].q = i[r].q || []).push(arguments);
            },
            i[r].l = 1 * new Date();
            a = s.createElement(o),
            m = s.getElementsByTagName(o)[0];
            a.async = 1;
            a.src = g;
            m.parentNode.insertBefore(a, m);
        }(window, document, 'script', scriptUrl, 'tv'));
        /* eslint-enable */
    },

    getIdPrefix() {
        return globalsService.isProd() ? '' : `${globalsService.getDeployEnvironment()}_`;
    },

    getProctorSessionId() {
        const bookedSessionId = settingsReader.getExternalBookedProctoringSessionId();
        if (bookedSessionId) {
            return bookedSessionId;
        }

        const sessionType = settingsReader.getExternalRemoteProctoringType();
        if ([talViewProctoringType.RECORDED, talViewProctoringType.RECORD_AND_REVIEW].includes(sessionType)) {
            return `${this.getIdPrefix()}${sessionReader.getAssessmentRunId()}`;
        }

        const logData = `proctor type: ${sessionType}, assessmentRunId: ${sessionReader.getAssessmentRunId()}, userId: ${sessionReader.getUserId()}`;
        console.warn(`talview session id is missing, ${logData}`);

        return null;
    },

    getSessionConfig() {
        const secureBrowserParams = {};
        if (settingsReader.isExternalRemoteProctoringSecureBrowserRequired()) {
            secureBrowserParams.enforceTSB = 'true';
        }
        return {
            profileId: `${this.getIdPrefix()}${sessionReader.getUserId()}`,
            session: this.getProctorSessionId(),
            sessionTitle: assessmentReader.getAssessmentTitle(),
            sessionType: settingsReader.getExternalRemoteProctoringType(),
            token: settingsReader.getExternalRemoteProctoringToken(),
            ...secureBrowserParams,
        };
    },

    createProctoringSession() {
        const {
            profileId,
            session,
            sessionTitle,
            sessionType,
            token,
            enforceTSB,
        } = this.getSessionConfig();

        window.tv('init', token, {
            sessionTitle,
            sessionType,
            profileId,
            session,
            enforceTSB,
            previewStyle: 'position: fixed; bottom: 0px;',
            initCallback(error, uuid) {
                if (error) {
                    console.error('Talview session creation failed: ', error);
                } else {
                    console.log('Talview session created, UUID: ', uuid);
                }
            },
            networkDisconnectionCallback() {
                console.log('Network disconnected, session paused');
                // Additional logic for handling network disconnection
            },
        });
    },

    initProctorClient() {
        return new Promise((resolve, reject) => {
            let initIntervalId;

            const maxInitTimeId = setTimeout(() => {
                clearInterval(initIntervalId);
                reject(`was not able to initialize ProctorClient3 for ${proctorClientMaxInitTime}ms`);
            }, proctorClientMaxInitTime);

            initIntervalId = setInterval(() => {
                if (window.ProctorClient3) {
                    clearInterval(initIntervalId);
                    clearTimeout(maxInitTimeId);
                    resolve(true);
                }

                console.log(`ProctorClient3 is not yet initialized, try again in ${clientPingInterval}ms`);
            }, clientPingInterval);
        });
    },

    createProctorClientEventListener() {
        window.ProctorClient3.on('log:event:all', (data) => {
            if (!hasTTJoinedSession) {
                const hasEventFromStartingListArrived = talViewService.isEventInStartingListOfEvents(data);
                if (hasEventFromStartingListArrived) {
                    const assessmentRunId = sessionReader.getAssessmentRunId();
                    hasTTJoinedSession = true;
                    console.log('talview session joined event arrived');
                    apiService.postProctoringSessionEstablishedNotice(assessmentRunId);
                }
                console.log(`talview event handled: ${data.description}`, data);
            }

            if (talViewService.hasTTJoinedSessionAndAllocatedTimePassedAndPassedErrorMessageDisplayed()) {
                talViewService.dispatchShouldDisplayTalviewInitAllocatedTimePassedErrorMessage(false);
            }
        });

        setTimeout(() => {
            console.log('10 seconds passed since ProctorClient3 init');
            hasTalviewInitAllocatedTimePassed = true;
            if (talViewService.hasTTNotJoinedSessionAndTalviewInitAllocatedTimePassed()) {
                talViewService.dispatchShouldDisplayTalviewInitAllocatedTimePassedErrorMessage(true);
            }
        }, sessionJoinedTimeoutTime);
    },

    async stopSession() {
        return new Promise((resolve) => {
            window.ProctorClient3.stop(resolve);
        });
    },

    isTalViewEnabled() {
        return !!settingsReader.getExternalRemoteProctoringType() && !!settingsReader.getExternalRemoteProctoringToken();
    },

    shouldRedirectToSecureBrowser() {
        return settingsReader.isExternalRemoteProctoringSecureBrowserRequired() && !utilService.isInsideProviewSecureBrowser();
    },

    redirectToSecureBrowser() {
        const marketplace = marketplaceSettingsService.getMarketplace();
        let redirectUrl = `${location.origin}/student`;
        if (!globalsService.isProd() && !!marketplace) {
            redirectUrl += `?marketplace=${marketplace}`;
        }

        const url = `${safeBrowserLinks.TALVIEW_BROWSER_DOWNLOAD_URL}?redirect_url=${encodeURIComponent(redirectUrl)}`;
        navigationService.navigateToUrl(url);
    },

    isEventInStartingListOfEvents(eventData) {
        // event_id: 3 - event_name: "Session Joined"
        // event_id: 4 - event_name: "Window Focus In"
        // event_id: 5 - event_name: "Window Focus Out"
        // event_id: 13 - event_name: "Recording Started"
        // event_id: 26 - event_name: "Camera Test Passed"
        // event_id: 34 - event_name: "Audio Test Passed"
        // event_id: 60 - event_name: "Camera Detected"
        // event_id: 149 - event_name: "Screen Capture Enabled"
        // event_id: 154 - event_name: "Face capture success"
        // event_id: 156 - event_name: "ID card capture success"
        // event_id: 159 - event_name: "Audio device detected"
        // event_id: 191 - event_name: "Candidate on Good Bandwidth"
        // event_id: 207 - event_name: "Multiple monitors detected"

        const talviewEventIdsList = [3, 4, 5, 13, 26, 34, 60, 149, 154, 156, 159, 191, 207];
        return talviewEventIdsList.includes(eventData.alert_type_id);
    },

    hasTTJoinedSessionAndAllocatedTimePassedAndPassedErrorMessageDisplayed() {
        return hasTTJoinedSession && hasTalviewInitAllocatedTimePassed && uiReader.isTalviewInitAllocatedTimePassedMessageDisplayed();
    },

    hasTTNotJoinedSessionAndTalviewInitAllocatedTimePassed() {
        return !hasTTJoinedSession && hasTalviewInitAllocatedTimePassed;
    },

    dispatchShouldDisplayTalviewInitAllocatedTimePassedErrorMessage(shouldDisplayInitAllocatedTimePassedErrorMessage) {
        storeManager.dispatch(uiActions.setIsTalviewInitAllocatedTimePassedErrorMessageDisplayed(shouldDisplayInitAllocatedTimePassedErrorMessage));
    },
};

export default talViewService;
